import type { TextImportDraft, TextImportExtractResponse } from '../types'

/**
 * In-memory (never serialised) holder for the text-import flow's step-1
 * draft and its most recent extraction. Lives outside React Router history
 * state on purpose: pasted source text can be a full book chapter, and
 * history entries have a size ceiling — putting it there works on a
 * paragraph and fails silently on a long paste.
 *
 * `entryKey` is the React Router `location.key` of the /text-import history
 * entry this draft belongs to. TextImportPage compares its own location.key
 * against this on mount to tell a genuine return (system back gesture,
 * browser back button, or the in-page Back control — all three land on the
 * same history entry and therefore the same key) from a fresh visit to the
 * flow (a new push always gets a new key), without needing to intercept or
 * synthesise any navigation.
 */

export interface CachedTextImportExtraction {
  text: string
  extractResponse: TextImportExtractResponse
}

interface TextImportFlowState {
  entryKey: string | null
  draft: TextImportDraft | null
  extraction: CachedTextImportExtraction | null
}

const state: TextImportFlowState = { entryKey: null, draft: null, extraction: null }

export function getTextImportFlowState(): Readonly<TextImportFlowState> {
  return state
}

export function setTextImportDraft(entryKey: string, draft: TextImportDraft): void {
  state.entryKey = entryKey
  state.draft = draft
}

export function setTextImportExtraction(entryKey: string, extraction: CachedTextImportExtraction | null): void {
  state.entryKey = entryKey
  state.extraction = extraction
}

/** Called on flow completion (deck created) or explicit abandonment (Close). */
export function clearTextImportFlow(): void {
  state.entryKey = null
  state.draft = null
  state.extraction = null
}
